import { reportPageActionToNewRelic } from '@verifime/utils';

/**
 * The New Relic page action for an expected business refusal: person-api answered, and refused for
 * a reason the customer is told (a duplicate identity, a document type the client has not opted
 * into). It is not an error, so it is not `noticeError`. A page action lets engineering count the
 * refusals.
 *
 * This code passes `source` (the screen that was refused) and `status` (the refusal kind), both
 * constant labels from the types below, and nothing else: the helper takes no identifier, name,
 * document number, email, error object or response body. The browser agent may add fields of its
 * own to the event; this code does not control them.
 *
 * A new refusal kind is added to `BUSINESS_REFUSAL_KINDS`. `useConfirmAction` reports every refusal
 * it shows, and its `TConfirmRefusal` type must stay within that list, so a kind that is shown and
 * not listed does not compile.
 */
export const BUSINESS_REFUSAL_PAGE_ACTION = 'business-refusal';

/** The screen whose action was refused. */
export type TBusinessRefusalSource = 'confirm-entity';

/** Why the action was refused. */
export const BUSINESS_REFUSAL_KINDS = [
  'duplicate-identity',
  'held-document-type-not-accepted',
] as const;
export type TBusinessRefusalKind = (typeof BUSINESS_REFUSAL_KINDS)[number];

/**
 * Reports one refusal. Call it once for each refusal person-api answers with, from the code that
 * handles the answer: never from a render or an effect, so a re-render does not report it again.
 * Returns true only when a live browser agent was called.
 */
export function reportBusinessRefusal(
  source: TBusinessRefusalSource,
  kind: TBusinessRefusalKind,
): boolean {
  return reportPageActionToNewRelic(BUSINESS_REFUSAL_PAGE_ACTION, { source, status: kind });
}
